import React, { createContext, useContext, useReducer } from "react";
import { mergeTerms } from "./configMergeUtils";
import { getExperimentBannerLink, getChannelID } from "../experimentation";
import { LanderConfig } from "../types/LanderConfig";

export enum LanderConfigAction {
  UPDATE_BANNER_AND_CHANNEL_BY_TREATMENT = "UPDATE_BANNER_AND_CHANNEL_BY_TREATMENT",
  UPDATE_CHANNEL_BY_TREATMENT = "UPDATE_CHANNEL_BY_TREATMENT",
  UPDATE_YAHOO_TYPE_TAG = "UPDATE_YAHOO_TYPE_TAG",
}

export interface TargetedConfig {
  fosUrl: string;
  [key: string]: any;
}

interface LanderConfigState {
  landerConfig: LanderConfig;
  queryConfig: object;
  mergedConfig: {
    keywords: string[];
  };
  targeted?: TargetedConfig;
}

interface LanderConfigContext extends LanderConfigState {
  landerConfigDispatch: React.Dispatch<{
    type: LanderConfigAction;
    payload?: any;
  }>;
}

export const ConfigContext = createContext<LanderConfigContext>(null);

export const useLanderConfig = () => {
  const ctx = useContext(ConfigContext);

  if (ctx == null) {
    throw new Error(
      "useLanderConfig must be called within LanderConfigProvider"
    );
  }

  return ctx;
};

export const initConfigState = ({
  landerConfig,
  queryConfig,
  targeted,
}: {
  landerConfig: LanderConfig;
  queryConfig: object;
  targeted?: TargetedConfig;
}): LanderConfigState => {
  // For targeted mode, landerConfig may be empty - skip mergeTerms
  const mergedConfig = {
    keywords: targeted ? [] : mergeTerms(landerConfig, queryConfig),
  };

  return {
    landerConfig,
    queryConfig,
    mergedConfig,
    targeted,
  };
};

export function configStateReducer(
  state: LanderConfigState,
  { type, payload }: { type: LanderConfigAction; payload?: any }
): LanderConfigState {
  const newState = JSON.parse(JSON.stringify(state));
  const inTreatmentCohort = payload?.inTreatmentCohort as boolean;

  switch (type) {
    case LanderConfigAction.UPDATE_BANNER_AND_CHANNEL_BY_TREATMENT: {
      newState.landerConfig.lander.banner.link = getExperimentBannerLink(
        inTreatmentCohort,
        state.landerConfig
      );
      newState.landerConfig.adSense.channel = getChannelID(
        inTreatmentCohort,
        state.landerConfig
      );
      return newState;
    }
    case LanderConfigAction.UPDATE_CHANNEL_BY_TREATMENT: {
      newState.landerConfig.adSense.channel = getChannelID(
        inTreatmentCohort,
        state.landerConfig
      );
      return newState;
    }
    case LanderConfigAction.UPDATE_YAHOO_TYPE_TAG: {
      if (!newState.landerConfig.yahoo) {
        newState.landerConfig.yahoo = {};
      }
      newState.landerConfig.yahoo.typeTag = payload?.typeTag;
      return newState;
    }
    default:
      return state;
  }
}

interface Props {
  landerConfig: LanderConfig;
  queryConfig: object;
  targeted?: TargetedConfig;
  children: React.ReactNode;
}

export const LanderConfigProvider = ({
  landerConfig,
  queryConfig,
  targeted,
  children,
}: Props) => {
  const [configState, configStateDispatch] = useReducer(
    configStateReducer,
    {
      landerConfig,
      queryConfig,
      targeted,
    },
    initConfigState
  );

  return (
    <ConfigContext.Provider
      value={{
        ...configState,
        landerConfigDispatch: configStateDispatch,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
